import { Modal, Button } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import PropTypes from 'prop-types'
import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react'
import { useDebouncedCallback } from 'use-debounce'

import Styles from './BusinessAdminModal.module.scss'
import useApi from '../../hooks/useApi'
import UserPill from '../user-pill'
import { useCurrentUser } from '../../hooks/useCurrentUser'

// backend returns 30 users per page right now, update if this changes in the future
const USERS_PER_PAGE = 30

export default function BusinessAdminModal({
  admins = [],
  isOpen,
  onAddAdmin,
  onClose,
  onRemoveAdmin,
}) {
  const { getPossibleAdmins } = useApi()
  const { t } = useTranslation()
  const { currentUser } = useCurrentUser()

  const [possibleAdmins, setPossibleAdmins] = useState([])
  const [moreAdminsToLoad, setMoreAdminsToLoad] = useState(true)
  const [page, setPage] = useState(1)
  const [isLoading, setIsLoading] = useState(false)
  const queryRef = useRef('')
  const isLoadingRef = useRef(false)

  const onToggleAdmin = useCallback(
    user => {
      if (admins.find(a => a.id === user.id)) {
        onRemoveAdmin(user)
      } else {
        if (admins.length < 5) {
          onAddAdmin(user)
        }
      }
    },
    [admins, onAddAdmin, onRemoveAdmin],
  )

  const performSearch = useCallback(
    async (searchQuery) => {
      if (isLoadingRef.current) return
      
      const trimmedQuery = searchQuery?.trim() || ''
      queryRef.current = trimmedQuery
      
      if (trimmedQuery.length >= 3) {
        isLoadingRef.current = true
        setIsLoading(true)
        try {
          const { data: users } = await getPossibleAdmins({
            groups: [],
            query: trimmedQuery,
            page: 1,
          })
          setPossibleAdmins(users || [])
          setMoreAdminsToLoad(users?.length === USERS_PER_PAGE)
          setPage(1)
        } catch (error) {
          console.error('Error searching users:', error)
          setPossibleAdmins([])
          setMoreAdminsToLoad(false)
        } finally {
          setIsLoading(false)
          isLoadingRef.current = false
        }
      } else {
        setPossibleAdmins([])
        setMoreAdminsToLoad(true)
      }
    },
    [getPossibleAdmins],
  )

  const debouncedSearch = useDebouncedCallback(
    performSearch,
    300,
    { leading: false, trailing: true }
  )

  const handleLoadMore = useCallback(async () => {
    if (!isLoading && !isLoadingRef.current && moreAdminsToLoad) {
      isLoadingRef.current = true
      setIsLoading(true)
      const nextPage = page + 1
      try {
        const { data: users } = await getPossibleAdmins({
          groups: [],
          query: queryRef.current || '',
          page: nextPage,
        })
        if (users) {
          setPossibleAdmins(prev => [...prev, ...users])
          setMoreAdminsToLoad(users.length === USERS_PER_PAGE)
          setPage(nextPage)
        }
      } catch (error) {
        console.error('Error loading more users:', error)
        setMoreAdminsToLoad(false)
      } finally {
        setIsLoading(false)
        isLoadingRef.current = false
      }
    }
  }, [page, isLoading, moreAdminsToLoad, getPossibleAdmins])

  const sortedAdmins = useMemo(() => {
    if (!admins || admins.length === 0) {
      return []
    }
    return [...admins].sort((a, b) => {
      const nameA = a?.name?.toLowerCase() || ''
      const nameB = b?.name?.toLowerCase() || ''
      return nameA.localeCompare(nameB)
    })
  }, [admins])

  useEffect(() => {
    // Reset state when modal opens or closes
    queryRef.current = ''
    setPage(1)
    setPossibleAdmins([])
    setMoreAdminsToLoad(true)
    setIsLoading(false)
    isLoadingRef.current = false
  }, [isOpen])

  return (
    <Modal show={isOpen} onHide={onClose}>
      <Modal.Header closeButton>
        <Modal.Title>{t('event.add_admins_title')}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {admins.length > 0 && (
          <div className={Styles.selectedAdmins}>
            {sortedAdmins.map(admin => (
              <UserPill key={admin.id} user={admin} onRemove={onRemoveAdmin} />
            ))}
          </div>
        )}

        <div className="mb-2">{t('event.add_admins_limit', { seats: 5 })}</div>

        <div className="mb-2">
          <input
            autoFocus
            className={Styles.input}
            onChange={e => {
              const value = e.target.value
              queryRef.current = value
              if (value.trim().length >= 3) {
                debouncedSearch(value)
              } else {
                setPossibleAdmins([])
                setMoreAdminsToLoad(true)
                debouncedSearch.cancel()
              }
            }}
            placeholder={t('users.edit.search')}
            type="text"
            disabled={isLoading}
          />
        </div>
        {isLoading && possibleAdmins.length === 0 && (
          <div className="text-center py-3">{t('event.loading')}</div>
        )}
        {possibleAdmins.length > 0 && (
          <div className="overflow-y-auto" style={{ maxHeight: '400px' }}>
            {possibleAdmins
              .filter(a => a.id !== currentUser.id)
              .map(user => (
                <div className="mb-2" key={user.id}>
                  <button
                    className={Styles.container}
                    type="button"
                    onClick={() => onToggleAdmin(user)}
                    disabled={admins.length >= 5 && !admins.find(a => a.id === user.id)}
                  >
                    <div
                      className={
                        admins.some(a => a.id === user.id)
                          ? Styles.radioActive
                          : Styles.radio
                      }
                    />
                    <img
                      alt={user.name}
                      className={Styles.avatar}
                      src={
                        user.media_attachment?.direct_upload_url ??
                        '/images/default-user.png'
                      }
                    />
                    <div className={Styles.nameContainer}>
                      <div className={Styles.name}>{user.name}</div>
                      <div className={Styles.nickname}>@{user.nickname}</div>
                    </div>
                  </button>
                </div>
              ))}
            {moreAdminsToLoad && (
              <div className="text-center mt-3 mb-3">
                <Button
                  variant="outline-secondary"
                  onClick={handleLoadMore}
                  disabled={isLoading}
                  size="sm"
                >
                  {isLoading ? t('event.loading') : t('event.load_more')}
                </Button>
              </div>
            )}
          </div>
        )}
      </Modal.Body>
    </Modal>
  )
}

BusinessAdminModal.propTypes = {
  admins: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      media_attachment: PropTypes.shape({
        direct_upload_url: PropTypes.string,
      }),
      name: PropTypes.string.isRequired,
      nickname: PropTypes.string,
    }),
  ),
  isOpen: PropTypes.bool.isRequired,
  onAddAdmin: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  onRemoveAdmin: PropTypes.func.isRequired,
}
